import _ from 'lodash';
import moment from 'moment';
// Bypasses the shared/ui barrel: routing through it would create an import cycle, since the
// barrel also re-exports FabricJS/AIECGViewer, which imports @/entities/ecg.
import { ANNOTATION_TYPE } from '@/shared/ui/components/FabricJS/Constants';
import { convertToMomentJs } from '@/shared/lib/aiUtils';

/**
 * Converts stored annotation timestamps into the sample coordinates a strip is drawn in. Deduplicates the list, then per type maps sample marks and patterns onto start and end samples clamped to the strip, rebuilds BCC x positions from xrTime, and rewrites the single x position of text and line annotations, reporting the sample mark that begins inside the strip.
 *
 * @param drawList - Stored annotations for the strip.
 * @param startStripEpoch - Epoch of the strip's first sample; a nil value keeps the stored sample positions.
 * @param samplingFrequency - Sampling rate used to turn milliseconds into samples.
 * @param stopStripEpoch - Epoch of the strip's last sample, used to clamp pattern edges.
 * @returns An object carrying the converted draw list and the sample mark's startSample.
 */
export const formatDrawDisplay = (
  drawList: any[],
  startStripEpoch: number,
  samplingFrequency: number,
  stopStripEpoch: number,
) => {
  if (_.isEmpty(drawList)) return { draw: [] as any[], startSample: 0 };
  let startSample;
  const isNillStart = _.isNil(startStripEpoch);
  const endStripSample =
    stopStripEpoch && startStripEpoch
      ? Math.round(((stopStripEpoch - startStripEpoch) / 1000) * samplingFrequency) - 1
      : null;
  // pre-existing bug: _.isEqual() is invoked (passing its boolean result as the comparator), not passed by reference
  const uniqDraw = _.uniqWith(drawList, (_.isEqual as any)());
  const draw = _.map(uniqDraw, (d) => {
    if (d.type === ANNOTATION_TYPE.SAMPLE_MARK) {
      const startSampleEpoch = moment(d.startTime).valueOf();
      const startSampleMark =
        d.startTime && !isNillStart
          ? Math.round(((startSampleEpoch - startStripEpoch) / 1000) * samplingFrequency)
          : d.startSample;
      const endSample =
        d.endTime && !isNillStart
          ? Math.round(((moment(d.endTime).valueOf() - startStripEpoch) / 1000) * samplingFrequency)
          : d.endSample;
      if (startSampleEpoch >= startStripEpoch && startSampleEpoch <= stopStripEpoch) {
        startSample = startSampleMark;
      }
      return {
        ...d,
        startSample: startSampleMark,
        endSample,
        sampleMarkRange: endSample - startSampleMark,
      };
    }
    if (d.type === ANNOTATION_TYPE.PATTERN) {
      const startSample =
        d.startTime && !isNillStart
          ? Math.round(
              ((moment(d.startTime).valueOf() - startStripEpoch) / 1000) * samplingFrequency,
            )
          : d.startSample;
      const endSample =
        d.endTime && !isNillStart
          ? Math.round(((moment(d.endTime).valueOf() - startStripEpoch) / 1000) * samplingFrequency)
          : d.endSample;
      return {
        ...d,
        startSample:
          startSample > endStripSample ? endStripSample : startSample < 0 ? 0 : startSample,
        endSample: endSample < 0 ? 0 : endSample > endStripSample ? endStripSample : endSample,
      };
    }
    if (d.type === ANNOTATION_TYPE.BCC) {
      const { xr } = d || {};
      const newXr = {};
      if (!_.isEmpty(d.xrTime) && !isNillStart) {
        _.forEach(Object.keys(d.xrTime), (key) => {
          const time = d.xrTime[key];
          _.assign(newXr, {
            [key]: Math.round(
              ((moment(time).valueOf() - startStripEpoch) / 1000) * samplingFrequency,
            ),
          });
        });
      }

      return { ...d, xr: d.xrTime ? newXr : xr };
    }
    if (
      d.type === ANNOTATION_TYPE.TEXT ||
      d.type === ANNOTATION_TYPE.HLINE ||
      d.type === ANNOTATION_TYPE.VLINE
    ) {
      let { xr } = d;
      if (!_.isEmpty(d.xrTime) && !isNillStart) {
        xr = ((moment(d.xrTime).valueOf() - startStripEpoch) / 1000) * samplingFrequency;
      }
      return { ...d, xr };
    }
    return d;
  });
  return { draw, startSample };
};

/**
 * Converts on-screen annotation sample positions back into timestamps for persistence. Turns the sample mark into the strip's start and stop times, converts pattern edges the same way, and keeps BCC, text and line annotations only while their x positions fall inside the sample mark, deduplicating the result.
 *
 * @param widgets - Annotations as drawn, positioned in samples.
 * @param samplingFrequency - Sampling rate used to turn samples into seconds.
 * @param startStripEpoch - Epoch of the strip's first sample.
 * @param timezoneOffset - Offset applied while building the ISO timestamps.
 * @param visibleChannels - Channel visibility recorded on the sample mark.
 * @returns An object carrying stripStart, stripStop, the converted formatDraw list, startSample and endSample.
 */
export const formatDrawMutation = (
  widgets: any[],
  samplingFrequency: number,
  startStripEpoch: number,
  timezoneOffset: number,
  visibleChannels?: any,
) => {
  const sampleMark = _.find(widgets, (item) => item.type === ANNOTATION_TYPE.SAMPLE_MARK);

  let stripStart;
  let stripStop;
  const { startSample, endSample, sampleMarkRange } = sampleMark || {};

  const formatDraw: any[] = [];
  _.forEach(widgets, (item) => {
    switch (item.type) {
      case ANNOTATION_TYPE.SAMPLE_MARK: {
        const totalSeconds = item.sampleMarkRange / samplingFrequency;
        const startSeconds = item.startSample / samplingFrequency;
        const startTime = convertToMomentJs(startStripEpoch, timezoneOffset)
          .add(startSeconds, 'seconds')
          .toISOString();
        const endTime = convertToMomentJs(startStripEpoch, timezoneOffset)
          .add(startSeconds + totalSeconds, 'seconds')
          .toISOString();
        formatDraw.push({
          ...item,
          startTime,
          endTime,
          visibleChannels,
        });
        stripStart = startTime;
        stripStop = endTime;
        break;
      }
      case ANNOTATION_TYPE.PATTERN: {
        const startTime = convertToMomentJs(startStripEpoch, timezoneOffset)
          .add(item.startSample / samplingFrequency, 'seconds')
          .toISOString();
        const endTime = convertToMomentJs(startStripEpoch, timezoneOffset)
          .add(item.endSample / samplingFrequency, 'seconds')
          .toISOString();
        formatDraw.push({
          ...item,
          startTime,
          endTime,
        });
        break;
      }
      case ANNOTATION_TYPE.BCC: {
        const xrTime = {};
        let inSampleMark = false;
        _.forEach(Object.keys(item.xr), (key) => {
          _.assign(xrTime, {
            [key]: convertToMomentJs(startStripEpoch, timezoneOffset)
              .add(item.xr[key] / samplingFrequency, 'seconds')
              .toISOString(),
          });
          const start = item.xr[key] - startSample;
          inSampleMark = start > 0 && start < sampleMarkRange;
        });
        if (inSampleMark) {
          formatDraw.push({
            ...item,
            xrTime,
          });
        }
        break;
      }
      case ANNOTATION_TYPE.TEXT:
      case ANNOTATION_TYPE.HLINE:
      case ANNOTATION_TYPE.VLINE: {
        const xr = item.xr - startSample;
        if (xr > 0 && xr < sampleMarkRange) {
          formatDraw.push({
            ...item,
            xrTime: convertToMomentJs(startStripEpoch, timezoneOffset)
              .add(item.xr / samplingFrequency, 'seconds')
              .toISOString(),
          });
        }
        break;
      }
      default:
        formatDraw.push(item);
        break;
    }
  });
  return {
    stripStart,
    stripStop,
    formatDraw: _.uniqWith(formatDraw, _.isEqual),
    startSample,
    endSample,
  };
};
